import React from "react";
import { OTelSpan } from "../types";
import { Bot, User, Settings, Database, Sparkles } from "lucide-react";

interface LLMSpanViewProps {
  span: OTelSpan;
}

const safeParseJSON = (str?: string) => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const RoleIcon = ({ role }: { role: string }) => {
  switch (role.toLowerCase()) {
    case "system":
      return <Settings className="w-4 h-4 text-[#64748b]" />;
    case "user":
      return <User className="w-4 h-4 text-blue-400" />;
    case "assistant":
      return <Bot className="w-4 h-4 text-purple-400" />;
    case "tool":
      return <Database className="w-4 h-4 text-success-400" />;
    case "thinking":
      return <Sparkles className="w-4 h-4 text-warning-400" />;
    default:
      return <div className="w-4 h-4 rounded-full bg-[#e2e8f0]" />;
  }
};

/** Extract content string from a pydantic-ai message part. */
function extractPartContent(part: any): string {
  if (typeof part === "string") return part;
  if (part?.content) return String(part.content);
  if (part?.text) return String(part.text);
  return JSON.stringify(part, null, 2);
}

/** Normalize messages from either OpenInference or pydantic-ai format. */
function extractMessages(
  attrs: Record<string, any>
): { role: string; content: string }[] {
  // OpenInference: llm.input_messages / llm.output_messages
  const oiInput = safeParseJSON(attrs["llm.input_messages"]);
  const oiOutput = safeParseJSON(attrs["llm.output_messages"]);
  if (Array.isArray(oiInput) || Array.isArray(oiOutput)) {
    const msgs: { role: string; content: string }[] = [];
    if (Array.isArray(oiInput)) {
      for (const m of oiInput) {
        msgs.push({
          role: m.role || "user",
          content: m.content || JSON.stringify(m),
        });
      }
    }
    if (Array.isArray(oiOutput)) {
      for (const m of oiOutput) {
        msgs.push({
          role: m.role || "assistant",
          content: m.content || JSON.stringify(m),
        });
      }
    }
    return msgs;
  }

  // pydantic-ai: pydantic_ai.all_messages (array of {role, parts})
  const paiMessages =
    typeof attrs["pydantic_ai.all_messages"] === "string"
      ? safeParseJSON(attrs["pydantic_ai.all_messages"])
      : attrs["pydantic_ai.all_messages"];

  if (Array.isArray(paiMessages)) {
    const msgs: { role: string; content: string }[] = [];

    // System instructions (may be separate)
    const sysInstructions =
      typeof attrs["gen_ai.system_instructions"] === "string"
        ? safeParseJSON(attrs["gen_ai.system_instructions"])
        : attrs["gen_ai.system_instructions"];
    if (Array.isArray(sysInstructions)) {
      for (const si of sysInstructions) {
        msgs.push({ role: "system", content: extractPartContent(si) });
      }
    }

    for (const m of paiMessages) {
      const role = m.role || "unknown";
      if (Array.isArray(m.parts)) {
        for (const part of m.parts) {
          const partType = part.type || "text";
          const content = extractPartContent(part);
          // Map pydantic-ai part types to display roles
          if (partType === "thinking") {
            msgs.push({ role: "thinking", content });
          } else {
            msgs.push({ role, content });
          }
        }
      } else {
        msgs.push({ role, content: m.content || JSON.stringify(m) });
      }
    }
    return msgs;
  }

  // Fallback: check for final_result / input.value / output.value
  const fallback: { role: string; content: string }[] = [];
  if (attrs["input.value"]) {
    fallback.push({ role: "user", content: String(attrs["input.value"]) });
  }
  if (attrs["final_result"]) {
    fallback.push({
      role: "assistant",
      content: String(attrs["final_result"]),
    });
  } else if (attrs["output.value"]) {
    fallback.push({
      role: "assistant",
      content: String(attrs["output.value"]),
    });
  }
  return fallback;
}

/** Extract token counts from either convention. */
function extractTokens(attrs: Record<string, any>): {
  prompt: number;
  completion: number;
  total: number;
} {
  const prompt =
    (attrs["llm.token_count.prompt"] as number) ??
    (attrs["gen_ai.usage.input_tokens"] as number) ??
    (attrs["gen_ai.usage.prompt_tokens"] as number) ??
    0;
  const completion =
    (attrs["llm.token_count.completion"] as number) ??
    (attrs["gen_ai.usage.output_tokens"] as number) ??
    (attrs["gen_ai.usage.completion_tokens"] as number) ??
    0;
  const total =
    (attrs["llm.token_count.total"] as number) ?? prompt + completion;
  return { prompt, completion, total };
}

export const LLMSpanView: React.FC<LLMSpanViewProps> = ({ span }) => {
  const messages = extractMessages(span.attributes);
  const tokens = extractTokens(span.attributes);

  const renderMessage = (
    msg: { role: string; content: string },
    index: number
  ) => {
    return (
      <div
        key={index}
        className="flex gap-4 p-4 border-b border-[#e2e8f0] hover:bg-[#f8fafc] transition-colors"
      >
        <div className="flex-shrink-0 mt-1">
          <RoleIcon role={msg.role} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#64748b]">
              {msg.role}
            </span>
          </div>
          <div className="text-sm text-[#0f172a] whitespace-pre-wrap break-words leading-relaxed">
            {msg.content}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Token Metrics Bar */}
      {tokens.total > 0 && (
        <div className="flex items-center gap-6 px-5 py-3 border-b border-[#e2e8f0] bg-white text-xs text-[#64748b]">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-wider font-semibold">
              Prompt
            </span>
            <span className="font-mono text-[#0f172a]">{tokens.prompt}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-wider font-semibold">
              Completion
            </span>
            <span className="font-mono text-[#0f172a]">
              {tokens.completion}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-wider font-semibold">
              Total
            </span>
            <span className="font-mono text-[#0f172a]">{tokens.total}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {messages.length > 0 ? (
          messages.map((msg, i) => renderMessage(msg, i))
        ) : (
          <div className="p-5 text-sm text-[#64748b]">
            No messages found in attributes.
          </div>
        )}
      </div>
    </div>
  );
};
